import { secureFetch } from './securityUtil.js';
import { Tooltip } from 'bootstrap';
import englishMessages from './languageTranslations/en.json';

// Alleen lokaal: op productie bestaat deze backend niet en gebruiken we de
// meegebouwde vertalingen meteen, zonder mislukte request.
const LANGUAGE_TRANSLATOR_API_URL = import.meta.env.DEV ? 'http://localhost:8081/api/language-translator' : null;

const localTranslations = import.meta.glob('./languageTranslations/*.json');
const messagesByLocale = new Map([['en', englishMessages]]);

let activeLocale = 'en';
let activeMessages = englishMessages;

function normalizeLocale(locale) {
  return String(locale || 'en').trim() || 'en';
}

function normalizeLanguageName(value) {
  return String(value || '').trim().toLowerCase();
}

function readSelectedContext() {
  const countrySelect = document.getElementById('country-select');
  const languageSelect = document.getElementById('language-select');
  const countryOption = countrySelect?.selectedOptions?.[0];
  const languageOption = languageSelect?.selectedOptions?.[0];

  return {
    countryId: countrySelect?.value || null,
    countryAbbreviation: countryOption?.dataset?.abbreviation || null,
    languageId: languageSelect?.value || null,
    languageName: languageOption?.dataset?.name || languageOption?.textContent || null
  };
}

export function resolveLocale(context = readSelectedContext()) {
  const country = String(context.countryAbbreviation || '').trim().toUpperCase();
  const countryId = String(context.countryId || '').trim();
  const languageName = normalizeLanguageName(context.languageName);
  const languageId = String(context.languageId || '').trim();

  if (languageId === '4' || languageName === 'english') {
    if (country === 'NL' || countryId === '1') return 'en-NL';
    if (country === 'BE' || countryId === '4') return 'en-BE';
    if (country === 'LXM' || country === 'LU' || countryId === '5') return 'en-LU';
    return 'en';
  }

  if (languageId === '1' || languageName === 'dutch') {
    if (country === 'BE' || countryId === '4') return 'nl-BE';
    return 'nl-NL';
  }

  if (languageId === '2' || languageName === 'french') {
    if (country === 'FR' || countryId === '3') return 'fr-FR';
    return 'fr-BE';
  }

  if (languageId === '3' || languageName === 'german') {
    if (country === 'GR' || country === 'DE' || countryId === '2') return 'de-DE';
    return 'de-BE';
  }

  if (languageId === '5' || languageName === 'luxembourgish') {
    return 'lb-LU';
  }

  return 'en';
}

async function loadLocalMessages(locale) {
  const normalized = normalizeLocale(locale);
  const loader = localTranslations[`./languageTranslations/${normalized}.json`]
    || localTranslations['./languageTranslations/en.json'];

  if (!loader) return {};

  const module = await loader();
  return module.default || module;
}

async function loadBackendMessages(locale, context) {
  const params = new URLSearchParams();

  if (locale) {
    params.set('locale', locale);
  } else if (context?.countryId && context?.languageId) {
    params.set('countryId', context.countryId);
    params.set('languageId', context.languageId);
  }

  if (!LANGUAGE_TRANSLATOR_API_URL) throw new Error('Backend translations disabled outside development');
  const response = await secureFetch(`${LANGUAGE_TRANSLATOR_API_URL}/messages?${params.toString()}`);
  if (!response.ok) {
    throw new Error(`Language translation request failed with status ${response.status}`);
  }

  const payload = await response.json();
  const messages = payload?.messages || payload || {};
  if (!messages || Object.keys(messages).length === 0) {
    console.warn('[LanguageTranslator] Empty backend translation payload', payload);
    throw new Error('Language translation response did not include messages');
  }

  return messages;
}

export async function loadLanguageTranslations(context = readSelectedContext()) {
  const locale = resolveLocale(context);
  const localMessages = await loadLocalMessages(locale);

  if (messagesByLocale.has(locale)) {
    activeLocale = locale;
    activeMessages = messagesByLocale.get(locale);
    applyLanguageTranslations(document);
    return activeMessages;
  }

  let messages;
  try {
    const backendMessages = await loadBackendMessages(locale, context);
    messages = { ...localMessages, ...backendMessages };
  } catch (error) {
    console.warn('[LanguageTranslator] Backend translations unavailable; using generated local translations.', error);
    messages = localMessages;
  }

  messagesByLocale.set(locale, messages);
  activeLocale = locale;
  activeMessages = messages;
  applyLanguageTranslations(document);
  return messages;
}

export function getActiveLocale() {
  return activeLocale;
}

export function translate(key, params = {}) {
  const template = activeMessages?.[key] || key;
  return Object.entries(params).reduce(
    (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
    template
  );
}

function setElementText(element, key) {
  let params = {};
  if (element.dataset.translateParams) {
    try {
      params = JSON.parse(element.dataset.translateParams);
    } catch (error) {
      console.warn('[LanguageTranslator] Invalid translate params', element.dataset.translateParams, error);
    }
  }

  const value = translate(key, params);
  if (value !== key || !element.textContent?.trim()) {
    element.textContent = value;
  }
}

function setElementAttribute(element, attribute, key) {
  const value = translate(key);
  if (value !== key || !element.getAttribute(attribute)) {
    element.setAttribute(attribute, value);
  }
  return value;
}

export function refreshBootstrapTooltip(element, title) {
  if (!element.matches?.('[data-bs-toggle="tooltip"]')) return;

  element.setAttribute('data-bs-original-title', title);

  const tooltip = Tooltip.getInstance(element);
  if (!tooltip) return;

  tooltip.setContent?.({ '.tooltip-inner': title });
}

export function applyLanguageTranslations(root = document) {
  root.querySelectorAll?.('[data-translate]').forEach(element => {
    setElementText(element, element.dataset.translate);
  });

  root.querySelectorAll?.('[data-translate-title]').forEach(element => {
    const key = element.dataset.translateTitle;
    const value = setElementAttribute(element, 'title', key);
    setElementAttribute(element, 'data-bs-title', key);
    refreshBootstrapTooltip(element, value);
  });

  root.querySelectorAll?.('[data-translate-placeholder]').forEach(element => {
    setElementAttribute(element, 'placeholder', element.dataset.translatePlaceholder);
  });

  root.querySelectorAll?.('[data-translate-aria-label]').forEach(element => {
    setElementAttribute(element, 'aria-label', element.dataset.translateAriaLabel);
  });

  if (document.documentElement) {
    document.documentElement.lang = activeLocale;
  }
}
